import { replaceUnderscoresWithSpaces } from '../../../utils/commonFunctions';

// Flattens nested field structure into flat array, detects dynamic fields and their dependencies
export function flattenFields(node: any, basePath = '', parentInputFields?: any) {
  const fields: any[] = [];
  const stack = [{ node, path: basePath, parentInputFields: parentInputFields || node }];

  while (stack.length > 0) {
    const { node: currentNode, path: currentPath, parentInputFields: currentParentInputFields } = stack.pop()!;

    if (!currentNode) continue;

    if (currentNode.datatype || currentNode.dataType) {
      const pathParts = currentPath.split('.');
      const originalKey = pathParts.pop() || currentPath;
      const name = currentPath.toLowerCase().replace(/\s+/g, '_');

      const displayLabel = replaceUnderscoresWithSpaces(originalKey || '');

      fields.push({
        type: currentNode.datatype || currentNode.dataType,
        unit: currentNode.unit,
        enum: currentNode.enum,
        valuesLabel: currentNode.valuesLabel || currentNode.valuesDisplay || [],
        min: currentNode.minimum,
        max: currentNode.maximum,
        name,
        label: displayLabel,
        originalKey
      });
    } else if (typeof currentNode === 'object') {
      const keys = Object.keys(currentNode);
      const pathParts = currentPath.split('.');
      
      // Generic detection: Check if this is a dynamic field object
      // Pattern: Object with keys that have enum/valuesLabel structure
      // This indicates a field that depends on another enum field's value
      const isDynamicFieldObject = keys.length > 0 && keys.every(key => {
        const value = currentNode[key];
        return typeof value === 'object' &&
               value !== null &&
               Array.isArray(value.enum) &&
               Array.isArray(value.valuesLabel) &&
               !(value.datatype || value.dataType);
      });

      if (isDynamicFieldObject) {
        // This is a dynamic field - find its dependency field
        // The dependency field should be an enum field in the same InputFields
        // whose enum values match the keys of this dynamic field
        const dynamicFieldKeys = keys.map(k => String(k));
        let dependencyField: any = null;
        
        // Search in parent InputFields for an enum field whose values match the dynamic field keys
        // The dependency field should have enum values that are present in the dynamic field keys
        if (currentParentInputFields && typeof currentParentInputFields === 'object') {
          const parentKeys = Object.keys(currentParentInputFields);
          let bestMatch: any = null;
          let bestMatchScore = 0;
          
          for (const parentKey of parentKeys) {
            const parentField = currentParentInputFields[parentKey];
            if (parentField && parentField.enum && Array.isArray(parentField.enum) && parentKey !== pathParts[pathParts.length - 1]) {
              // Check if enum values (as strings) are present in the dynamic field keys
              const parentEnumStrings = parentField.enum.map((v: any) => String(v));
              const matchingKeys = parentEnumStrings.filter(enumVal => dynamicFieldKeys.includes(enumVal));
              const matchScore = matchingKeys.length / parentEnumStrings.length;
              
              // If all enum values have corresponding keys in dynamic field, it's a perfect match
              // Or if most enum values match, it's likely the dependency
              if (matchScore > bestMatchScore && matchScore >= 0.5) {
                bestMatchScore = matchScore;
                // Construct the full path for dependency field name to match the flattened field name
                const dependencyPath = currentPath.split('.').slice(0, -1).join('.') + '.' + parentKey;
                const dependencyName = dependencyPath.toLowerCase().replace(/\s+/g, '_');
                bestMatch = {
                  originalKey: parentKey,
                  name: dependencyName
                };
              }
            }
          }
          
          if (bestMatch) {
            dependencyField = bestMatch;
          }
        }

        // Store as dynamic field
        const originalKey = pathParts.pop() || currentPath;
        const name = currentPath.toLowerCase().replace(/\s+/g, '_');
        const displayLabel = replaceUnderscoresWithSpaces(originalKey || '');

        fields.push({
          type: 'string',
          enum: [],
          valuesLabel: [],
          name,
          label: displayLabel,
          originalKey,
          isDynamicField: true,
          dynamicOptions: currentNode, // Store all dependency-specific options
          dependencyField: dependencyField // Store the field it depends on
        });
      } else {
        // Normal object traversal
        for (let i = keys.length - 1; i >= 0; i--) {
          const key = keys[i];
          stack.push({
            node: currentNode[key],
            path: currentPath ? `${currentPath}.${key}` : key,
            parentInputFields: currentParentInputFields
          });
        }
      }
    }
  }

  return fields;
}
